import { GetPublicOrganizationProductResponse, GetPublicOrganizationResponse, OrganizationSubscription, ProductPeriod, PurchaseSummaryResponse } from '../types';
import { LitElement, css, html } from 'lit';
import { cancel_subscription, getSubscriptionById } from '../helpers/api/purchases-api';
import { customElement, state } from 'lit/decorators';
import { formatPrice, getProductById } from '../helpers/api/products-api';

import { getPublicOrganizations } from '../helpers/api/organization-api';
import { getQueryStringParameters } from '../helpers/urls';
import { isDefined } from '../helpers/utils';

@customElement('subscription-details-page')
export class SubscriptionDetailsPage extends LitElement {
    static amplitude_callsite: string = 'Subscription Details Page';

    @state()
    loading: boolean = true;

    @state()
    subscriptionDetails?: OrganizationSubscription;

    @state()
    organization_details?: GetPublicOrganizationResponse;

    @state()
    product_details?: GetPublicOrganizationProductResponse;

    @state()
    transactions: PurchaseSummaryResponse[] = [];

    @state()
    error?: string;

    @state()
    cancelling: boolean = false;

    @state()
    cancelMessage?: string;

    @state()
    cancelError?: string;

    @state()
    subscription_id?: string;

    @state()
    organization_id?: string;

    async firstUpdated() {
        await this.startup();
    }

    private async startup() {
        const queryString = getQueryStringParameters();
        this.subscription_id = queryString.get('subscription_id') ?? undefined;
        this.organization_id = queryString.get('organization_id') ?? undefined;

        if (!isDefined(this.subscription_id) || !isDefined(this.organization_id)) {
            this.error = 'Missing subscription ID or organization ID.';
            this.loading = false;
            return;
        }

        try {
            // Load subscription and transaction details
            const response = await getSubscriptionById(this.subscription_id, this.organization_id);
            this.subscriptionDetails = response.organization_subscription;
            this.transactions = response.transaction_history;

            // Load organization details
            this.organization_details = (await getPublicOrganizations('SubscriptionDetailsPage', [this.organization_id]))[0];

            // Load product details if product_id exists
            if (this.subscriptionDetails.product_id?.$oid) {
                this.product_details = await getProductById(this.subscriptionDetails.product_id.$oid);
            }

        } catch (error) {
            console.error('Failed to load subscription details:', error);
            this.error = 'Unable to load subscription details. Please try again later.';
        } finally {
            this.loading = false;
        }
        window.dispatchEvent(new CustomEvent('page-content-ready'));
    }

    private async cancelSubscription() {
        if (!isDefined(this.subscriptionDetails) || !isDefined(this.subscription_id) || !isDefined(this.organization_id)) {
            this.cancelError = 'Missing subscription details to cancel.';
            return;
        }

        this.cancelling = true;
        this.cancelError = undefined;
        this.cancelMessage = undefined;

        try {
            await cancel_subscription(
                this.organization_id,
                this.subscription_id,
            );
            this.cancelMessage = 'Subscription cancelled successfully. You will not be billed again.';

            // Reload subscription details to reflect cancellation
            this.subscriptionDetails = (await getSubscriptionById(this.subscription_id, this.organization_id)).organization_subscription;
        } catch (error: any) {
            this.cancelError = error?.message ?? `Unable to cancel the subscription right now please contact support ${this.subscriptionDetails.support_email ?? ''}.`;
        } finally {
            this.cancelling = false;
        }
    }

    private getStatusBadgeClass(): string {
        const status = this.subscriptionDetails?.status.toLowerCase();
        if (status === 'active') return 'status-active';
        if (status === 'cancelled' || status === 'canceled') return 'status-cancelled';
        return 'status-other';
    }

    private getIntervalText(): string {
        const interval = this.subscriptionDetails?.interval;
        if (!interval) return 'Monthly';

        switch (interval) {
            case ProductPeriod.Monthly:
                return 'Monthly';
            case ProductPeriod.Yearly:
                return 'Yearly';
            case ProductPeriod.Weekly:
                return 'Weekly';
            default:
                return 'Monthly';
        }
    }

    private navigateToReceipt(transactionId: string) {
        window.location.href = `#checkout-return?return_status=receipt&pending_transaction_id=${transactionId}`;
    }

    private contactOwner() {
        if (isDefined(this.subscriptionDetails?.support_email)) {
            window.location.href = `mailto:${this.subscriptionDetails?.support_email ?? ''}`;
        }
    }

    render() {
        if (this.loading) {
            return html`
                <div class="container">
                    <loading-element loadingString="Loading subscription details..."></loading-element>
                </div>
            `;
        }

        if (this.error) {
            return html`
                <div class="container">
                    <div class="error-card">
                        <h1>⚠️ Error</h1>
                        <p>${this.error}</p>
                    </div>
                </div>
            `;
        }

        const isActive = this.subscriptionDetails?.status.toLowerCase() === 'active';
        const isCancelled = this.subscriptionDetails?.status.toLowerCase() === 'cancelled' ||
                           this.subscriptionDetails?.status.toLowerCase() === 'canceled';

        return html`
            <div class="subscription-container">
                <setting-card class="subscription-header-card">
                    <div class="header-content">
                        <h1>Subscription Details</h1>
                        <div class="organization-name">
                            ${this.organization_details?.name ?? 'Proxi Maps Services'}
                        </div>
                        <div class="status-badge ${this.getStatusBadgeClass()}">
                            ${this.subscriptionDetails?.status ?? 'Unknown'}
                        </div>
                    </div>
                </setting-card>

                <setting-card class="subscription-info-card">
                    <h2>Subscription Information</h2>
                    ${this.product_details ? html`
                        <div class="product-info">
                            <h3 class="product-name">${this.product_details.name}</h3>
                            <p class="product-description">${this.product_details.description}</p>
                        </div>
                    ` : ''}
                    <div class="info-grid">
                        <div class="info-row">
                            <span class="label">Billing Interval:</span>
                            <span class="value">${this.getIntervalText()}</span>
                        </div>
                        <div class="info-row">
                            <span class="label">Price:</span>
                            <span class="value">${formatPrice(this.subscriptionDetails?.price_cents ?? 0, this.subscriptionDetails?.currency ?? 'USD')} / ${this.getIntervalText().toLowerCase()}</span>
                        </div>
                        <div class="info-row">
                            <span class="label">Email:</span>
                            <span class="value">${this.subscriptionDetails?.buyer_email ?? 'N/A'}</span>
                        </div>
                        <div class="info-row">
                            <span class="label">Started:</span>
                            <span class="value">${new Date(this.subscriptionDetails?.created_at ?? Date.now()).toLocaleDateString()}</span>
                        </div>
                        ${isCancelled && this.subscriptionDetails?.cancelled_at ? html`
                            <div class="info-row">
                                <span class="label">Cancelled:</span>
                                <span class="value">${new Date(this.subscriptionDetails.cancelled_at).toLocaleDateString()}</span>
                            </div>
                        ` : ''}
                    </div>

                    ${this.cancelMessage ? html`
                        <div class="message success-message">${this.cancelMessage}</div>
                    ` : ''}

                    ${this.cancelError ? html`
                        <div class="message error-message">${this.cancelError}</div>
                    ` : ''}

                    <div class="action-buttons">
                        ${isActive ? html`
                            <button
                                class="cancel-button"
                                ?disabled=${this.cancelling}
                                @click=${this.cancelSubscription}>
                                ${this.cancelling ? 'Cancelling...' : 'Cancel Subscription'}
                            </button>
                        ` : ''}
                        <button class="secondary-button" @click=${this.contactOwner}>
                            Contact Support
                        </button>
                    </div>
                </setting-card>

                <setting-card class="transactions-card">
                    <h2>Transaction History</h2>
                    ${this.transactions.length === 0 ? html`
                        <p class="no-transactions">No transactions found for this subscription.</p>
                    ` : html`
                        <div class="transactions-list">
                            ${this.transactions.map(transaction => html`
                                <div
                                    class="transaction-item"
                                    @click=${() => this.navigateToReceipt(transaction.pending_transaction_id?.$oid ?? '')}>
                                    <div class="transaction-date">
                                        ${new Date(transaction.updated_at ?? Date.now()).toLocaleDateString()}
                                    </div>
                                    <div class="transaction-amount">
                                        ${formatPrice(transaction.total_price_cents ?? 0, transaction.currency ?? 'USD')}
                                    </div>
                                    <div class="transaction-status ${transaction.status?.toLowerCase() ?? 'unknown'}">
                                        ${transaction.status ?? 'Unknown'}
                                    </div>
                                    <div class="transaction-arrow">→</div>
                                </div>
                            `)}
                        </div>
                    `}
                </setting-card>
            </div>
        `;
    }

    static styles = css`
        :host {
            display: block;
            width: 100%;
            min-height: 100vh;
            background: #f5f5f5;
            padding: 2rem 1rem;
            overflow-y: auto;
        }

        .subscription-container {
            max-width: 800px;
            margin: 0 auto;
            display: flex;
            flex-direction: column;
            gap: 1.5rem;
        }

        setting-card {
            background: white;
            border: 1px solid #e0e0e0;
            border-radius: 8px;
            padding: 2rem;
        }

        .subscription-header-card {
            text-align: center;
        }

        .header-content h1 {
            margin: 0 0 0.5rem 0;
            font-size: 2rem;
            color: #1f2937;
        }

        .organization-name {
            font-size: 1.2rem;
            color: #6b7280;
            margin-bottom: 1rem;
        }

        .status-badge {
            display: inline-block;
            padding: 0.5rem 1rem;
            border-radius: 20px;
            font-weight: 600;
            text-transform: uppercase;
            font-size: 0.875rem;
        }

        .status-active {
            background: #d1fae5;
            color: #065f46;
        }

        .status-cancelled {
            background: #fee2e2;
            color: #991b1b;
        }

        .status-other {
            background: #e5e7eb;
            color: #374151;
        }

        h2 {
            margin: 0 0 1.5rem 0;
            font-size: 1.5rem;
            color: #1f2937;
        }

        .product-info {
            margin-bottom: 1.5rem;
            padding-bottom: 1.5rem;
            border-bottom: 2px solid #e5e7eb;
        }

        .product-name {
            margin: 0 0 0.5rem 0;
            font-size: 1.25rem;
            font-weight: 700;
            color: #1f2937;
        }

        .product-description {
            margin: 0;
            font-size: 1rem;
            color: #6b7280;
            line-height: 1.5;
        }

        .info-grid {
            display: flex;
            flex-direction: column;
            gap: 1rem;
        }

        .info-row {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 0.75rem 0;
            border-bottom: 1px solid #f0f0f0;
        }

        .info-row:last-child {
            border-bottom: none;
        }

        .label {
            color: #6b7280;
            font-weight: 500;
        }

        .value {
            color: #1f2937;
            font-weight: 600;
        }

        .message {
            margin: 1rem 0;
            padding: 1rem;
            border-radius: 6px;
            font-size: 0.95rem;
        }

        .success-message {
            background: #d1fae5;
            color: #065f46;
        }

        .error-message {
            background: #fee2e2;
            color: #991b1b;
        }

        .action-buttons {
            display: flex;
            gap: 1rem;
            margin-top: 1.5rem;
            flex-wrap: wrap;
        }

        .cancel-button {
            background: #ef4444;
            color: white;
            border: none;
            padding: 0.75rem 1.5rem;
            font-size: 1rem;
            border-radius: 6px;
            cursor: pointer;
            font-weight: 600;
            transition: background 0.2s;
        }

        .cancel-button:hover:not(:disabled) {
            background: #dc2626;
        }

        .cancel-button:disabled {
            opacity: 0.6;
            cursor: not-allowed;
        }

        .secondary-button {
            background: #6b7280;
            color: white;
            border: none;
            padding: 0.75rem 1.5rem;
            font-size: 1rem;
            border-radius: 6px;
            cursor: pointer;
            font-weight: 600;
            transition: background 0.2s;
        }

        .secondary-button:hover {
            background: #4b5563;
        }

        .no-transactions {
            text-align: center;
            color: #6b7280;
            padding: 2rem;
        }

        .transactions-list {
            display: flex;
            flex-direction: column;
            gap: 0.5rem;
        }

        .transaction-item {
            display: grid;
            grid-template-columns: 1fr auto auto auto;
            align-items: center;
            gap: 1rem;
            padding: 1rem;
            border: 1px solid #e5e7eb;
            border-radius: 6px;
            cursor: pointer;
            transition: all 0.2s;
        }

        .transaction-item:hover {
            border-color: #667eea;
            background: #f9fafb;
            transform: translateX(4px);
        }

        .transaction-date {
            color: #374151;
            font-weight: 500;
        }

        .transaction-amount {
            color: #1f2937;
            font-weight: 700;
        }

        .transaction-status {
            padding: 0.25rem 0.75rem;
            border-radius: 12px;
            font-size: 0.85rem;
            font-weight: 600;
            text-transform: capitalize;
        }

        .transaction-status.completed {
            background: #d1fae5;
            color: #065f46;
        }

        .transaction-status.pending {
            background: #fef3c7;
            color: #92400e;
        }

        .transaction-status.failed {
            background: #fee2e2;
            color: #991b1b;
        }

        .transaction-arrow {
            color: #667eea;
            font-size: 1.5rem;
            font-weight: bold;
        }

        .error-card {
            background: white;
            border: 1px solid #e0e0e0;
            border-radius: 8px;
            padding: 2rem;
            text-align: center;
            max-width: 600px;
            margin: 0 auto;
        }

        .error-card h1 {
            color: #dc2626;
            margin: 0 0 1rem 0;
        }

        @media (max-width: 640px) {
            :host {
                padding: 1rem;
            }

            setting-card {
                padding: 1.5rem;
            }

            .header-content h1 {
                font-size: 1.5rem;
            }

            h2 {
                font-size: 1.25rem;
            }

            .transaction-item {
                grid-template-columns: 1fr;
                gap: 0.5rem;
            }

            .transaction-arrow {
                display: none;
            }

            .action-buttons {
                flex-direction: column;
            }

            .cancel-button,
            .secondary-button {
                width: 100%;
            }
        }
    `;
}
